import api from './api';
import authStorage from '../utils/authStorage';

/**
 * Admin authentication service
 */
const authService = {
    /**
     * Login admin
     * @param {string} email
     * @param {string} password
     * @returns {Promise<Object>} Admin data and token
     */
    async login(email, password, rememberMe = false) {
        const response = await api.post('/auth/admin/login', {
            email,
            password,
            remember_me: rememberMe
        });

        if (response.data.success) {
            const data = response.data.data;

            if (data.requires_2fa) {
                return { requires_2fa: true, temp_token: data.temp_token };
            }

            const { token, admin, remember_me } = data;

            // Persist auth based on remember-me preference.
            authStorage.saveAuth(token, admin, remember_me ?? rememberMe);

            return { token, admin };
        }

        throw new Error(response.data.message || 'Login failed');
    },

    /**
     * Verify 2FA token
     * @param {string} token - 2FA token
     * @param {string} temp_token - Temporary token from login
     * @returns {Promise<Object>} Admin data and final token
     */
    async verify2FA(token, temp_token) {
        const response = await api.post('/auth/admin/login/verify-2fa', { token, temp_token });

        if (response.data.success) {
            const { token: finalToken, admin, remember_me } = response.data.data;

            authStorage.saveAuth(finalToken, admin, remember_me);

            return { token: finalToken, admin };
        }

        throw new Error(response.data.message || '2FA verification failed');
    },

    /**
     * Logout admin
     */
    async logout() {
        try {
            await api.post('/auth/admin/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            authStorage.clearAuth();
        }
    },

    /**
     * Get current admin profile
     * @returns {Promise<Object>} Admin profile
     */
    async getProfile() {
        const response = await api.get('/auth/admin/me');
        return response.data.data.admin;
    },

    /**
     * Update current admin profile
     * @param {Object} data - Profile data
     * @returns {Promise<Object>} Updated admin profile
     */
    async updateProfile(data) {
        const response = await api.put('/auth/admin/me', data);
        return response.data.data.admin;
    },

    /**
     * Check if user is authenticated
     * @returns {boolean}
     */
    isAuthenticated() {
        const token = authStorage.getToken();
        return !!token;
    },

    /**
     * Get stored admin data
     * @returns {Object|null}
     */
    getCurrentAdmin() {
        return authStorage.getCachedAdmin();
    },

    /**
     * Check if current admin is Super Admin
     * @returns {boolean}
     */
    isSuperAdmin() {
        const admin = this.getCurrentAdmin();
        return admin?.role === 'super_admin';
    },

    /**
     * Setup 2FA
     * @returns {Promise<Object>} Secret and QR code
     */
    async setup2FA() {
        const response = await api.get('/auth/admin/2fa/setup');
        return response.data.data;
    },

    /**
     * Enable 2FA
     * @param {string} token - 2FA token
     * @returns {Promise<Object>}
     */
    async enable2FA(token) {
        const response = await api.post('/auth/admin/2fa/enable', { token });
        return response.data;
    },

    /**
     * Disable 2FA
     * @param {string} token - 2FA token
     * @returns {Promise<Object>}
     */
    async disable2FA(token) {
        const response = await api.post('/auth/admin/2fa/disable', { token });
        return response.data;
    }
};

export default authService;
